import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Shield, BookOpen, TrendingUp, Users, FileCheck, ExternalLink } from 'lucide-react';
import { DiplomaticCarousel } from '../components/DiplomaticCarousel';

export default function Home() {
  const { language, t } = useLanguage();

  const assets = {
    bookCover: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/tysf0jqk_COUVERTURE%20PICSU.png',
    chru1: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/kaitr28n_image%20CHRu%201.png',
    chru2: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/9mcfnvna_Image%20CHRu%202.png',
    splitPayment: 'https://static.prod-images.emergentagent.com/jobs/7cf829f0-16ef-4383-a637-469dccd1e538/images/a86ae6ce1c391b744544e7162e0adf4bc6bfea1538c43bb1958dfd934f9f0a87.png',
    frenchMethod: 'https://static.prod-images.emergentagent.com/jobs/7cf829f0-16ef-4383-a637-469dccd1e538/images/fe2b253ee8bfe9821d916bac0fe3601db668ca6070ed58881539ce577e6a95cf.png',
    petitPatriote: 'https://static.prod-images.emergentagent.com/jobs/7cf829f0-16ef-4383-a637-469dccd1e538/images/978e3eee130409c8599a9809fcd06bec2fa0c558698e35c43bc14e3b0fbe42ed.png',
    heroImage: 'https://images.pexels.com/photos/3115739/pexels-photo-3115739.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  };

  // Traductions pour les blocs stratégiques
  const strategicBlocks = {
    title: {
      fr: "Nos Preuves d'Excellence",
      en: "Our Proofs of Excellence",
      pt: "Nossas Provas de Excelência",
      it: "Le Nostre Prove di Eccellenza",
      ru: "Наши доказательства превосходства",
      zh: "我们的卓越证明"
    },
    subtitle: {
      fr: "Découvrez les fondements institutionnels de LENODA",
      en: "Discover LENODA's institutional foundations",
      pt: "Descubra os fundamentos institucionais da LENODA",
      it: "Scopri le fondamenta istituzionali di LENODA",
      ru: "Откройте для себя институциональные основы LENODA",
      zh: "探索LENODA的制度基础"
    },
    book: {
      title: { fr: "L'Ouvrage PICSU", en: "PICSU Book", pt: "Livro PICSU", it: "Libro PICSU", ru: "Книга PICSU", zh: "PICSU著作" },
      desc: { fr: "Le manifeste qui réinvente la santé africaine", en: "The manifesto reinventing African healthcare", pt: "O manifesto que reinventa a saúde africana", it: "Il manifesto che reinventa la sanità africana", ru: "Манифест, переосмысливающий африканское здравоохранение", zh: "重塑非洲医疗的宣言" },
      cta: { fr: "Découvrir le livre", en: "Discover the book", pt: "Descobrir o livro", it: "Scopri il libro", ru: "Открыть книгу", zh: "了解更多" }
    },
    council: {
      title: { fr: "Conseil Scientifique", en: "Scientific Council", pt: "Conselho Científico", it: "Consiglio Scientifico", ru: "Научный совет", zh: "科学委员会" },
      desc: { fr: "Experts internationaux garantissant l'excellence", en: "International experts guaranteeing excellence", pt: "Especialistas internacionais garantindo excelência", it: "Esperti internazionali che garantiscono l'eccellenza", ru: "Международные эксперты, гарантирующие превосходство", zh: "保证卓越的国际专家" },
      cta: { fr: "Voir les experts", en: "View experts", pt: "Ver especialistas", it: "Vedi esperti", ru: "Посмотреть экспертов", zh: "查看专家" }
    },
    proofs: {
      title: { fr: "Mur de Preuves", en: "Wall of Proof", pt: "Muro de Provas", it: "Muro delle Prove", ru: "Стена доказательств", zh: "证明墙" },
      desc: { fr: "Documents officiels et validations gouvernementales", en: "Official documents and government validations", pt: "Documentos oficiais e validações governamentais", it: "Documenti ufficiali e validazioni governative", ru: "Официальные документы и государственные валидации", zh: "官方文件和政府认证" },
      cta: { fr: "Voir les preuves", en: "View proofs", pt: "Ver provas", it: "Vedi prove", ru: "Посмотреть доказательства", zh: "查看证明" }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="relative min-h-screen flex items-center overflow-hidden pt-12" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src={assets.heroImage}
            alt="Modern architecture" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <div className="max-w-3xl">
            {/* Badge Cabinet d'Ingénierie */}
            <div className="inline-block mb-4 px-4 py-2 bg-emerald-600/20 border border-emerald-400/30 rounded-full backdrop-blur-sm">
              <p className="text-sm font-semibold text-emerald-300 uppercase tracking-wider">
                {language === 'fr' ? "Cabinet d'Ingénierie en Souveraineté" :
                 language === 'en' ? "Sovereignty Engineering Firm" :
                 language === 'pt' ? "Gabinete de Engenharia em Soberania" :
                 language === 'it' ? "Studio di Ingegneria della Sovranità" :
                 language === 'ru' ? "Кабинет инженерии суверенитета" :
                 "主权工程咨询公司"}
              </p>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight" data-testid="hero-title" style={{fontFamily: 'Playfair Display, serif'}}>
              {t('hero.title')} <span className="text-sky-400">{t('hero.titleBold')}</span> {t('hero.titleEnd')}
            </h1>
            
            <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-6" data-testid="hero-subtitle">
              {t('hero.subtitle')}
            </p>

            {/* Rôle LENODA */}
            <div className="mb-10 p-4 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm">
              <p className="text-sm text-slate-300">
                <span className="text-emerald-400 font-semibold">LENODA</span> – 
                {language === 'fr' ? " Intégrateur & Maître d'Ouvrage Délégué | Garanties Décennales & RC Pro Internationales" :
                 language === 'en' ? " Integrator & Delegated Project Owner | Ten-Year Guarantees & International Professional Liability" :
                 language === 'pt' ? " Integrador & Dono de Obra Delegado | Garantias Decenais & RC Pro Internacionais" :
                 language === 'it' ? " Integratore & Committente Delegato | Garanzie Decennali & RC Pro Internazionali" :
                 language === 'ru' ? " Интегратор и Делегированный заказчик | Десятилетние гарантии и международная профессиональная ответственность" :
                 " 集成商和委托业主 | 十年保证和国际专业责任"}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/about" 
                data-testid="hero-cta-discover"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-900 rounded-full font-medium hover:bg-slate-100 transition-all group"
              >
                {t('hero.cta1')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/deal-room" 
                data-testid="hero-cta-dealroom"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-600 text-white border border-emerald-500 rounded-full font-medium hover:bg-emerald-700 transition-colors"
              >
                {t('hero.cta2')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCS STRATÉGIQUES - Accès rapide aux preuves */}
      <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-800" data-testid="strategic-blocks-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
              {strategicBlocks.title[language] || strategicBlocks.title.fr}
            </h2>
            <p className="text-base text-slate-400">
              {strategicBlocks.subtitle[language] || strategicBlocks.subtitle.fr}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Bloc 1: Ouvrage PICSU */}
            <Link 
              to="/book-picsu"
              data-testid="strategic-block-book"
              className="group relative bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-8 overflow-hidden hover:scale-[1.02] transition-all shadow-xl hover:shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/30 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {strategicBlocks.book.title[language] || strategicBlocks.book.title.fr}
                </h3>
                <p className="text-sm text-emerald-100 mb-6">
                  {strategicBlocks.book.desc[language] || strategicBlocks.book.desc.fr}
                </p>
                <div className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
                  {strategicBlocks.book.cta[language] || strategicBlocks.book.cta.fr}
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </Link>

            {/* Bloc 2: Conseil Scientifique */}
            <Link 
              to="/scientific-council"
              data-testid="strategic-block-council"
              className="group relative bg-gradient-to-br from-sky-600 to-sky-700 rounded-2xl p-8 overflow-hidden hover:scale-[1.02] transition-all shadow-xl hover:shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-sky-500/30 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {strategicBlocks.council.title[language] || strategicBlocks.council.title.fr}
                </h3>
                <p className="text-sm text-sky-100 mb-6">
                  {strategicBlocks.council.desc[language] || strategicBlocks.council.desc.fr}
                </p>
                <div className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
                  {strategicBlocks.council.cta[language] || strategicBlocks.council.cta.fr}
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </Link>

            {/* Bloc 3: Mur de Preuves */}
            <Link 
              to="/validations"
              data-testid="strategic-block-proofs"
              className="group relative bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-8 overflow-hidden hover:scale-[1.02] transition-all shadow-xl hover:shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/30 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                  <FileCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {strategicBlocks.proofs.title[language] || strategicBlocks.proofs.title.fr}
                </h3>
                <p className="text-sm text-amber-100 mb-6">
                  {strategicBlocks.proofs.desc[language] || strategicBlocks.proofs.desc.fr}
                </p>
                <div className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
                  {strategicBlocks.proofs.cta[language] || strategicBlocks.proofs.cta.fr}
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-slate-50" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              {t('pillars.title')}
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              {t('pillars.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Link 
              to="/health" 
              data-testid="pillar-card-health"
              className="group bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all"
            >
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-sky-200 transition-colors">
                <Shield className="w-8 h-8 text-sky-600" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
                {t('pillars.health.title')}
              </h3>
              <p className="text-sm font-medium text-sky-600 uppercase tracking-wider mb-4">
                {t('pillars.health.subtitle')}
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                {t('pillars.health.description')}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-sky-600 font-medium group-hover:gap-3 transition-all">
                {t('pillars.learnMore') || 'En savoir plus'} <ArrowRight className="w-5 h-5" />
              </div>
            </Link>

            <Link 
              to="/agriculture" 
              data-testid="pillar-card-agriculture"
              className="group bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all"
            >
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-emerald-200 transition-colors">
                <TrendingUp className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
                {t('pillars.agriculture.title')}
              </h3>
              <p className="text-sm font-medium text-emerald-600 uppercase tracking-wider mb-4">
                {t('pillars.agriculture.subtitle')}
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                {t('pillars.agriculture.description')}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-emerald-600 font-medium group-hover:gap-3 transition-all">
                {t('pillars.learnMore') || 'En savoir plus'} <ArrowRight className="w-5 h-5" />
              </div>
            </Link>

            <Link 
              to="/education" 
              data-testid="pillar-card-education"
              className="group bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all"
            >
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-slate-200 transition-colors">
                <BookOpen className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
                {t('pillars.education.title')}
              </h3>
              <p className="text-sm font-medium text-slate-600 uppercase tracking-wider mb-4">
                {t('pillars.education.subtitle')}
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                {t('pillars.education.description')}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-slate-600 font-medium group-hover:gap-3 transition-all">
                {t('pillars.learnMore') || 'En savoir plus'} <ArrowRight className="w-5 h-5" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      <DiplomaticCarousel />

      <section className="py-24 md:py-32 bg-white" data-testid="credibility-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              {t('credibility.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="border-l-4 border-sky-600 pl-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {t('credibility.inpi')}
                </h3>
                <p className="text-base text-slate-600 mb-1">
                  {t('credibility.inpiDesc')}
                </p>
                <p className="text-sm font-mono text-sky-600">
                  {t('credibility.inpiNumber')}
                </p>
              </div>

              <div className="border-l-4 border-emerald-600 pl-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {t('credibility.council')}
                </h3>
                <p className="text-base text-slate-600">
                  {t('credibility.councilDesc')}
                </p>
              </div>
            </div>

            <div className="relative group" data-testid="book-showcase">
              <div className="aspect-square rounded-2xl overflow-hidden border border-slate-200">
                <img 
                  src={assets.bookCover}
                  alt="PICSU Book Cover" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="mt-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {t('credibility.bookTitle')}
                </h3>
                <p className="text-base text-slate-600 mb-4">
                  {t('credibility.bookAuthor')}
                </p>
                <a 
                  href="https://www.amazon.fr/PICSU-nouveau-chapitre-sant%C3%A9-mondiale-ebook/dp/B0CRHLGTHX" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="amazon-book-link"
                  className="inline-flex items-center gap-2 text-sky-600 font-medium hover:gap-3 transition-all"
                >
                  {t('credibility.bookCta')} <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-slate-900 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold mb-6">
            {t('investors.title')}
          </h2>
          <p className="text-base md:text-lg text-slate-300 mb-10">
            {t('investors.subtitle')}
          </p>
          <Link 
            to="/deal-room" 
            data-testid="cta-dealroom-button"
            className="inline-flex items-center gap-2 px-8 py-4 bg-sky-600 text-white rounded-full font-medium hover:bg-sky-700 transition-colors"
          >
            {t('investors.dealRoomCta')} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
